"use client"

import * as React from "react"
import { Button } from "@/components/ui/button"
import { StatusBadge } from "@/components/ui/status-badge"
import { Tip } from "@/components/ui/tip"
import {
  FloatingSheetPanel,
  FloatingSheetPanelBody,
  FloatingSheetPanelContent,
  FloatingSheetPanelHeader,
  FloatingSheetPanelToolbar,
  railTriggerProps,
} from "@/lib/floating-sheet-panel"
import { DashboardSectionTitle } from "@/components/dashboard-section-heading"
import {
  QUALITY_THEMES,
  QUALITY_VOICE_ATTENTION,
  QUALITY_VOICE_IMPROVING,
  type QualityThemeChip,
  type QualityVoiceItem,
} from "@/lib/mock/evaluation-quality-dashboard"

function themeChipAriaLabel(chip: QualityThemeChip): string {
  const sentiment =
    chip.sentiment === "up"
      ? "sentiment improving"
      : chip.sentiment === "down"
        ? "sentiment declining"
        : "sentiment unchanged"
  return `${chip.label}, ${chip.count} mentions, ${sentiment}`
}

function sentimentTrendLabel(sentiment: QualityThemeChip["sentiment"]): string | null {
  if (sentiment === "up") return "Sentiment improving"
  if (sentiment === "down") return "Sentiment declining"
  return null
}

export function QualityLearnerVoice() {
  const [theme, setTheme] = React.useState<QualityThemeChip | null>(null)
  const [shown, setShown] = React.useState<QualityThemeChip | null>(null)

  if (theme && theme !== shown) setShown(theme)

  const shownIndex = shown ? QUALITY_THEMES.findIndex(chip => chip.id === shown.id) : -1

  return (
    <>
      <section aria-labelledby="quality-voice-heading" className="flex h-full flex-col gap-4">
        <div className="flex items-end justify-between gap-3">
          <DashboardSectionTitle as="h2" id="quality-voice-heading">
            Learner voice
          </DashboardSectionTitle>
          <Button variant="ghost" size="sm">
            Explore themes
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <VoiceList
            title="Improving"
            titleId="quality-voice-improving"
            items={QUALITY_VOICE_IMPROVING}
            tone="positive"
          />
          <VoiceList
            title="Needs attention"
            titleId="quality-voice-attention"
            items={QUALITY_VOICE_ATTENTION}
            tone="negative"
          />
        </div>

        <div
          role="group"
          aria-label="Learner voice themes"
          className="flex flex-wrap gap-2"
        >
          {QUALITY_THEMES.map(chip => {
            const trendLabel = sentimentTrendLabel(chip.sentiment)
            return (
              <button
                key={chip.id}
                type="button"
                {...railTriggerProps()}
                onClick={() => setTheme(chip)}
                aria-pressed={theme?.id === chip.id}
                aria-label={themeChipAriaLabel(chip)}
                className="inline-flex min-h-8 items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-sm font-normal text-foreground outline-none transition-colors hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span>{chip.label}</span>
                <span className="tabular-nums text-muted-foreground">{chip.count}</span>
                {trendLabel ? (
                  <Tip label={trendLabel}>
                    <span className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground">
                      <i
                        className={
                          chip.sentiment === "up"
                            ? "fa-light fa-arrow-trend-up text-chart-2 text-xs"
                            : "fa-light fa-arrow-trend-down text-destructive-ink text-xs"
                        }
                        aria-hidden="true"
                      />
                    </span>
                  </Tip>
                ) : null}
              </button>
            )
          })}
        </div>
      </section>

      <FloatingSheetPanel open={theme != null} onOpenChange={open => !open && setTheme(null)}>
        <FloatingSheetPanelContent size="sm" contentSlot="quality-theme-detail">
          <FloatingSheetPanelToolbar
            onPrevious={
              shownIndex > 0 ? () => setTheme(QUALITY_THEMES[shownIndex - 1]!) : undefined
            }
            onNext={
              shownIndex >= 0 && shownIndex < QUALITY_THEMES.length - 1
                ? () => setTheme(QUALITY_THEMES[shownIndex + 1]!) : undefined
            }
            previousLabel="Previous theme"
            nextLabel="Next theme"
          />
          <FloatingSheetPanelHeader
            title={shown?.label ?? "Theme detail"}
            description={shown?.detail?.headline}
            subtitle={
              shownIndex >= 0 ? `${shownIndex + 1} of ${QUALITY_THEMES.length}` : undefined
            }
          />
          <FloatingSheetPanelBody>
            {shown?.detail ? (
              <div className="flex flex-col gap-3 px-4 pb-6 text-sm">
                <div>
                  <p className="mb-1.5 text-muted-foreground">Most affected</p>
                  <ul className="flex list-disc flex-col gap-1 ps-4 text-foreground">
                    {shown.detail.affected.map(row => (
                      <li key={row}>{row}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}
          </FloatingSheetPanelBody>
        </FloatingSheetPanelContent>
      </FloatingSheetPanel>
    </>
  )
}

function VoiceList({
  title,
  titleId,
  items,
  tone,
}: {
  title: string
  titleId: string
  items: QualityVoiceItem[]
  tone: "positive" | "negative"
}) {
  const badgeTone = tone === "positive" ? "success" : "warning"
  const trendLabel = tone === "positive" ? "Increasing" : "Declining"

  return (
    <div className="rounded-xl border border-border/60 bg-muted/20 p-3">
      <h3
        id={titleId}
        className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground"
      >
        {title}
      </h3>
      <ul aria-labelledby={titleId} className="flex flex-col gap-2">
        {items.map(item => (
          <li key={item.label} className="flex items-center justify-between gap-2 text-sm">
            <span className="text-foreground">{item.label}</span>
            <StatusBadge
              label={trendLabel}
              tone={badgeTone}
              icon={item.direction === "up" ? "fa-arrow-trend-up" : "fa-arrow-trend-down"}
              size="md"
            />
          </li>
        ))}
      </ul>
    </div>
  )
}
